import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { ConversationDto, MessageDto } from '@initiative/shared';

/**
 * Offline layer.
 *
 * Chats and the most recent messages are cached so the app opens instantly (and
 * readable) without a network, while unsent messages wait in an outbox that is
 * flushed as soon as the connection is back – including media captured offline.
 */

export interface OutboxAttachment {
  kind: 'image' | 'video' | 'audio' | 'file' | 'sticker';
  mime: string;
  fileName: string;
  blob: Blob;
  width?: number;
  height?: number;
  durationMs?: number;
  waveform?: number[];
  previewDataUrl?: string;
  /**
   * Die Sitzung beim Hochladen in Stücken – damit nach einem Netzabriss oder
   * Neustart dort weitergemacht wird, wo der Server steht.
   */
  stueckSitzung?: string;
  /**
   * Gesetzt, wenn der Blob NICHT in IndexedDB liegt (siehe `OUTBOX_BLOB_MAX`).
   * Merkt sich, woran die Datei beim erneuten Auswählen zu erkennen ist.
   */
  grosseDatei?: { groesse: number; geaendert?: number };
  /** Nach einem Neustart: Die grosse Datei ist weg und muss neu gewählt werden. */
  blobFehlt?: boolean;
  /** Schon fertig hochgeladen (grosse Anhänge) – nicht noch einmal senden. */
  hochgeladenId?: string;
}

/**
 * Ab dieser Grösse wandert eine Datei NICHT in die Outbox-Datenbank.
 *
 * # Warum
 *
 * Ein Blob in IndexedDB ist eine Kopie: Chrome und Safari schreiben ihn in
 * ihre eigene Ablage. Bei einem Video von zwei Gigabyte hiesse das, vor dem
 * ersten gesendeten Byte zwei Gigabyte auf dem Telefon zu kopieren – und auf
 * einem fast vollen iPhone scheitert das Speichern einfach, und mit ihm die
 * Nachricht. OPFS wäre dieselbe Kopie unter anderem Namen.
 *
 * # Was stattdessen
 *
 * Die Datei bleibt nur im Arbeitsspeicher dieses Tabs (als Verweis auf die
 * Datei im Gerät – ein `File` aus der Auswahl ist kein Inhalt, sondern ein
 * Zeiger). Gemerkt werden Name, Grösse und die Sitzung beim Server. Geht die
 * App zu, fehlt danach der Zeiger; die Blase bittet dann darum, die Datei
 * noch einmal auszuwählen, prüft die Grösse und macht beim Server dort
 * weiter, wo er stand – ohne ein schon empfangenes Stück erneut zu senden.
 *
 * Unterhalb der Grenze bleibt alles wie bisher: Ein Foto oder ein kurzes
 * Video übersteht den Neustart vollständig.
 */
export const OUTBOX_BLOB_MAX = 200 * 1024 * 1024;

/** Die grossen Dateien, die nur im Speicher dieses Tabs liegen. */
const fluechtig = new Map<string, Blob>();

function fluechtigSchluessel(clientId: string, index: number): string {
  return `${clientId}:${index}`;
}

/** Eine neu ausgewählte Datei für einen liegengebliebenen Upload hinterlegen. */
export function dateiHinterlegen(clientId: string, index: number, datei: Blob): void {
  fluechtig.set(fluechtigSchluessel(clientId, index), datei);
}

export function fluechtigVergessen(clientId: string): void {
  for (const schluessel of [...fluechtig.keys()]) {
    if (schluessel.startsWith(`${clientId}:`)) fluechtig.delete(schluessel);
  }
}

/** Für die Datenbank: grosse Blobs durch einen leeren ersetzen und merken. */
export function zumSpeichern(entry: OutboxEntry): OutboxEntry {
  return {
    ...entry,
    pendingAttachments: entry.pendingAttachments.map((anhang, index) => {
      if (anhang.blob.size <= OUTBOX_BLOB_MAX && !anhang.grosseDatei) return anhang;
      if (anhang.blob.size > 0) dateiHinterlegen(entry.clientId, index, anhang.blob);
      const groesse = anhang.grosseDatei?.groesse ?? anhang.blob.size;
      const geaendert =
        anhang.grosseDatei?.geaendert ??
        (anhang.blob instanceof File ? anhang.blob.lastModified : undefined);
      const { blobFehlt: _weg, ...rest } = anhang;
      return { ...rest, blob: new Blob([]), grosseDatei: { groesse, geaendert } };
    }),
  };
}

/** Aus der Datenbank: grosse Blobs aus dem Speicher dieses Tabs zurückholen. */
export function ausDemSpeicher(entry: OutboxEntry): OutboxEntry {
  return {
    ...entry,
    pendingAttachments: entry.pendingAttachments.map((anhang, index) => {
      if (!anhang.grosseDatei) return anhang;
      const datei = fluechtig.get(fluechtigSchluessel(entry.clientId, index));
      return datei ? { ...anhang, blob: datei, blobFehlt: false } : { ...anhang, blobFehlt: true };
    }),
  };
}

export interface OutboxEntry {
  clientId: string;
  conversationId: string;
  type: MessageDto['type'];
  body: string | null;
  replyToId: string | null;
  metadata: MessageDto['metadata'];
  attachmentIds: string[];
  pendingAttachments: OutboxAttachment[];
  createdAt: number;
  attempts: number;
  lastError: string | null;
}

/**
 * Ein liegengebliebener Entwurf aus dem Fotoeditor.
 *
 * Was hier NICHT drinsteht, ist das Bild. Ein Foto von zwölf Megapunkten
 * wäre ein Vielfaches von allem anderen in dieser Datenbank, und es liegt ja
 * schon irgendwo – als Anhang auf dem Server oder als Datei im Gerät. Der
 * Entwurf ist die ANWEISUNG dazu, in genau der Form, in der sie auch
 * verschickt wird (`docNachRoh` in `rezept.ts`).
 *
 * Der Schlüssel ist deshalb eine Kennung des BILDES und nicht des Editors:
 * Wer dasselbe Foto wieder aufmacht, soll seinen Entwurf wiederfinden, egal
 * über welchen Weg er hereinkommt.
 */
export interface Bildentwurf {
  /** Kennung des Bildes – siehe `bildKennung` in `entwurf.ts`. */
  id: string;
  /** Das Dokument als reines JSON. */
  doc: unknown;
  /** Die Masse, zu denen es gehört. Passen sie nicht, gilt der Entwurf nicht. */
  breite: number;
  hoehe: number;
  /** Wann zuletzt geschrieben, in Millisekunden. */
  stand: number;
  name: string | null;
}

interface InitiativeDB extends DBSchema {
  conversations: { key: string; value: ConversationDto };
  entwuerfe: { key: string; value: Bildentwurf };
  messages: {
    key: string;
    value: MessageDto;
    indexes: { 'by-conversation': string };
  };
  outbox: { key: string; value: OutboxEntry; indexes: { 'by-conversation': string } };
  meta: { key: string; value: unknown };
}

let dbPromise: Promise<IDBPDatabase<InitiativeDB>> | null = null;

function getDb(): Promise<IDBPDatabase<InitiativeDB>> {
  if (!dbPromise) {
    /*
     * Fassung 2 bringt `entwuerfe`.
     *
     * `upgrade` läuft bei JEDER Erhöhung und legt nur an, was fehlt – die
     * vorhandenen Abfragen bleiben unangetastet. Wer die Zahl vergisst und
     * nur den Aufruf hinzufügt, bekommt einen Speicher, den es in schon
     * geöffneten Datenbanken nie geben wird.
     */
    dbPromise = openDB<InitiativeDB>('initiative', 2, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('conversations')) {
          db.createObjectStore('conversations', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('messages')) {
          const store = db.createObjectStore('messages', { keyPath: 'id' });
          store.createIndex('by-conversation', 'conversationId');
        }
        if (!db.objectStoreNames.contains('outbox')) {
          const store = db.createObjectStore('outbox', { keyPath: 'clientId' });
          store.createIndex('by-conversation', 'conversationId');
        }
        if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta');
        if (!db.objectStoreNames.contains('entwuerfe')) {
          db.createObjectStore('entwuerfe', { keyPath: 'id' });
        }
      },
    }).catch((error) => {
      // Safari private mode and locked-down browsers: degrade to online-only.
      console.warn('IndexedDB unavailable, running without offline cache', error);
      throw error;
    });
  }
  return dbPromise;
}

async function safe<T>(operation: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await operation();
  } catch {
    return fallback;
  }
}

export async function cacheConversations(conversations: ConversationDto[]): Promise<void> {
  await safe(async () => {
    const db = await getDb();
    const tx = db.transaction('conversations', 'readwrite');
    await Promise.all(conversations.map((conversation) => tx.store.put(conversation)));
    await tx.done;
  }, undefined);
}

export async function readCachedConversations(): Promise<ConversationDto[]> {
  return safe(async () => {
    const db = await getDb();
    const items = await db.getAll('conversations');
    return items.sort(
      (a, b) =>
        new Date(b.lastMessage?.createdAt ?? b.updatedAt).getTime() -
        new Date(a.lastMessage?.createdAt ?? a.updatedAt).getTime(),
    );
  }, []);
}

export async function removeCachedConversation(id: string): Promise<void> {
  await safe(async () => {
    const db = await getDb();
    await db.delete('conversations', id);
    const keys = await db.getAllKeysFromIndex('messages', 'by-conversation', id);
    const tx = db.transaction('messages', 'readwrite');
    await Promise.all(keys.map((key) => tx.store.delete(key)));
    await tx.done;
  }, undefined);
}

const MESSAGE_CACHE_PER_CHAT = 200;

export async function cacheMessages(messages: MessageDto[]): Promise<void> {
  if (messages.length === 0) return;
  await safe(async () => {
    const db = await getDb();
    const tx = db.transaction('messages', 'readwrite');
    await Promise.all(messages.map((message) => tx.store.put(message)));
    await tx.done;
  }, undefined);
}

export async function readCachedMessages(conversationId: string): Promise<MessageDto[]> {
  return safe(async () => {
    const db = await getDb();
    const items = await db.getAllFromIndex('messages', 'by-conversation', conversationId);
    return items.sort((a, b) => a.id.localeCompare(b.id)).slice(-MESSAGE_CACHE_PER_CHAT);
  }, []);
}

export async function dropCachedMessage(id: string): Promise<void> {
  await safe(async () => {
    const db = await getDb();
    await db.delete('messages', id);
  }, undefined);
}

export async function trimMessageCache(conversationId: string): Promise<void> {
  await safe(async () => {
    const db = await getDb();
    const keys = await db.getAllKeysFromIndex('messages', 'by-conversation', conversationId);
    if (keys.length <= MESSAGE_CACHE_PER_CHAT) return;
    const stale = keys.sort().slice(0, keys.length - MESSAGE_CACHE_PER_CHAT);
    const tx = db.transaction('messages', 'readwrite');
    await Promise.all(stale.map((key) => tx.store.delete(key)));
    await tx.done;
  }, undefined);
}

export async function enqueueOutbox(entry: OutboxEntry): Promise<void> {
  await safe(async () => {
    const db = await getDb();
    await db.put('outbox', zumSpeichern(entry));
  }, undefined);
}

export async function readOutbox(conversationId?: string): Promise<OutboxEntry[]> {
  return safe(async () => {
    const db = await getDb();
    const items = conversationId
      ? await db.getAllFromIndex('outbox', 'by-conversation', conversationId)
      : await db.getAll('outbox');
    return items.map(ausDemSpeicher).sort((a, b) => a.createdAt - b.createdAt);
  }, []);
}

export async function updateOutbox(entry: OutboxEntry): Promise<void> {
  await enqueueOutbox(entry);
}

export async function removeOutbox(clientId: string): Promise<void> {
  fluechtigVergessen(clientId);
  await safe(async () => {
    const db = await getDb();
    await db.delete('outbox', clientId);
  }, undefined);
}

export async function readMeta<T>(key: string): Promise<T | null> {
  return safe(async () => {
    const db = await getDb();
    return ((await db.get('meta', key)) as T) ?? null;
  }, null);
}

export async function writeMeta(key: string, value: unknown): Promise<void> {
  await safe(async () => {
    const db = await getDb();
    await db.put('meta', value, key);
  }, undefined);
}

/** Called on logout so no chat data survives for the next account. */
export async function clearOfflineData(): Promise<void> {
  await safe(async () => {
    const db = await getDb();
    await Promise.all([
      db.clear('conversations'),
      db.clear('messages'),
      db.clear('outbox'),
      db.clear('meta'),
      // Auch die Entwürfe: Sie tragen zwar kein Bild, aber sie verraten, was
      // jemand an einem Bild gemacht hat – wo ein Balken lag, was
      // weggeschnitten wurde.
      db.clear('entwuerfe'),
    ]);
  }, undefined);
}

/* ---------- Entwürfe aus dem Fotoeditor ---------- */

/**
 * Wieviele Entwürfe höchstens liegenbleiben, und wie lange.
 *
 * Beides, weil beides allein danebengeht: Eine reine Anzahl hielte einen
 * Entwurf von vor zwei Jahren, ein reines Alter liesse hundert Entwürfe eines
 * geschäftigen Tages nebeneinander stehen. Ein Entwurf mit Netzmaske ist
 * einige Dutzend Kilobyte gross.
 */
const ENTWUERFE_MAX = 20;
const ENTWURF_TAGE = 30;

export async function entwurfLesen(id: string): Promise<Bildentwurf | null> {
  return safe(async () => {
    const db = await getDb();
    return (await db.get('entwuerfe', id)) ?? null;
  }, null);
}

export async function entwurfSchreiben(entwurf: Bildentwurf): Promise<void> {
  await safe(async () => {
    const db = await getDb();
    await db.put('entwuerfe', entwurf);
    // Aufräumen im selben Zug: Ein eigener Zeitgeber dafür wäre ein Dienst,
    // der läuft, wenn niemand ihn braucht.
    const alle = await db.getAll('entwuerfe');
    const grenze = Date.now() - ENTWURF_TAGE * 24 * 60 * 60 * 1000;
    const weg = alle
      .filter((e) => e.stand < grenze)
      .concat(
        alle
          .filter((e) => e.stand >= grenze)
          .sort((a, b) => b.stand - a.stand)
          .slice(ENTWUERFE_MAX),
      );
    await Promise.all(weg.map((e) => db.delete('entwuerfe', e.id)));
  }, undefined);
}

export async function entwurfLoeschen(id: string): Promise<void> {
  await safe(async () => {
    const db = await getDb();
    await db.delete('entwuerfe', id);
  }, undefined);
}
